import { usePageMeta } from "@/utils/usePageMeta";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ContentCard from "@/components/ContentCard";
import { Heart, Repeat, ShoppingBag, ArrowRight } from "lucide-react";
import { useTranslatedObjects } from "@/hooks/useTranslatedContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: "hjalp-i-vardagen", rubrik: "Hjälp i vardagen", desc: "Praktisk hjälp i vardagen. Behöver du en hand \u2013 eller har du en att räcka ut?", icon: Heart },
  { slug: "hand-mot-hand", rubrik: "Hand mot hand", desc: "Byt tjänster och kunskap utan pengar. Här möts man på lika villkor.", icon: Repeat },
  { slug: "kop-salj", rubrik: "Köp & sälj lokalt", desc: "Köp, sälj eller skänk saker lokalt. Tryggt, nära och modererat.", icon: ShoppingBag },
];

export default function Anslagstavla() {
  const [recent, setRecent] = useState([]);
  const [arkiv, setArkiv] = useState([]);
  const [visaArkiv, setVisaArkiv] = useState(false);

  usePageMeta({
    title: "Anslagstavla – vidalv.live",
    description: "Lokala annonser, tips och meddelanden från Backe, Rossön och bygden vid Fjällsjöälven. Köp & sälj, efterlyst, tjänster och hyresobjekt – modererad och utan reklam.",
    path: "/anslagstavla",
  });

  useEffect(() => {
    axios.get(`${API}/anslag`).then(r => setRecent(r.data.slice(0, 5))).catch(() => {});
    axios.get(`${API}/anslag/arkiv`).then(r => setArkiv(r.data)).catch(() => {});
  }, []);

  const [recentT] = useTranslatedObjects(recent, ["titel", "beskrivning"]);

  return (
    <div data-testid="anslagstavla-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Anslagstavla</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3">Det som händer i bygden</h1>
      <p className="text-sm text-bark mb-8 max-w-2xl leading-relaxed">
        Här samlas det som behövs, erbjuds och söks. Alla anslag granskas av vårt lokala team innan de publiceras.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-12">
        {CATEGORIES.map((cat) => (
          <Link
            key={cat.slug}
            to={`/anslagstavla/${cat.slug}`}
            data-testid={`category-${cat.slug}`}
            className="block bg-parchment border border-warm-border rounded-sm p-5 hover:border-bark/40 transition-all no-underline group"
          >
            <cat.icon size={28} className="text-pine mb-3" />
            <h2 className="font-heading text-lg font-medium text-ink mb-2 group-hover:text-falu transition-colors">
              {cat.rubrik}
            </h2>
            <p className="text-base text-muted-foreground mb-3">{cat.desc}</p>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-falu">
              Visa anslag <ArrowRight size={16} />
            </span>
          </Link>
        ))}
      </div>

      <section data-testid="recent-anslag">
        <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-4">Senast på anslagstavlan</h2>
        <div className="flex flex-col gap-4">
          {recentT.map((item) => (
            <ContentCard key={item.id} item={{ ...item, typ: "anslag" }} />
          ))}
        </div>

        {arkiv.length > 0 && (
          <div data-testid="arkiv-toggle" className="mt-6">
            <button
              onClick={() => setVisaArkiv(v => !v)}
              data-testid="toggle-anslag-arkiv"
              className="text-[11px] font-body font-semibold text-bark/55 hover:text-ink uppercase tracking-[0.18em] border-b border-dashed border-bark/30 pb-0.5 transition-colors"
            >
              {visaArkiv ? "Göm utgångna anslag" : `Visa utgångna anslag (${arkiv.length})`}
            </button>
            {visaArkiv && (
              <div data-testid="arkiv-list" className="mt-4 flex flex-col gap-3">
                {arkiv.map(a => (
                  <div key={a.id} data-testid={`arkiv-${a.id}`} className="bg-bone-alt/50 border border-warm-border/60 rounded-sm p-4 opacity-70">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[10px] font-body font-semibold uppercase tracking-[0.2em] text-falu bg-falu/10 px-2 py-0.5 rounded-sm">Utgånget</span>
                      {a.rubrik && <span className="text-[11px] text-bark/60">{a.rubrik}</span>}
                      {a.giltigt_tom && <span className="text-[11px] text-bark/50">gick ut {a.giltigt_tom}</span>}
                    </div>
                    <h3 className="font-heading text-base font-semibold text-ink/80">{a.titel}</h3>
                    {a.ort && <p className="text-xs text-bark/60 mt-1">{a.ort}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <div data-testid="cta-anslagstavla" className="mt-12 bg-pine rounded-sm p-6 sm:p-8">
        <h2 className="font-heading text-xl font-semibold text-parchment mb-2">Har du något att dela med bygden?</h2>
        <p className="text-sm text-parchment/70 mb-4">Skicka in ditt anslag &ndash; vi granskar och publicerar det åt dig.</p>
        <Link
          to="/skicka-anslag"
          data-testid="cta-anslagstavla-skicka"
          className="inline-flex items-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline"
        >
          Skicka in anslag
        </Link>
      </div>
    </div>
  );
}
